import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Form, Alert } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import { getUserByUid, createOrUpdateUserWithUid } from '../services/authService';
import ThemeLogo from './ThemeLogo';

export default function Profile() {
  const [profile, setProfile] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    street: "",
    postCode: "",
    county: "",
    city: "",
  });
  const [showSuccess, setShowSuccess] = useState(false);
  const [activeTab, setActiveTab] = useState("profile");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchProfile = async () => {
      setLoading(true);
      setError("");
      try {
        const user = auth.currentUser;
        if (!user) {
          setError("You must be logged in to view this page.");
          setLoading(false);
          return;
        }
        const data = await getUserByUid(user.uid);
        if (data) {
          setProfile({
            firstName: data.firstName || "",
            lastName: data.lastName || "",
            email: data.email || "",
            phone: data.phone || "",
            street: data.streetAddress || "",
            postCode: data.postCode || "",
            county: data.county || "",
            city: data.city || "",
          });
        }
      } catch (err) {
        setError("Failed to load profile.");
      } finally {
        setLoading(false);
      }
    };
    fetchProfile();
  }, []);

  const handleChange = (e) => {
    setProfile({ ...profile, [e.target.name]: e.target.value });
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setShowSuccess(false);
    setError("");
    try {
      const user = auth.currentUser;
      if (!user) {
        setError("You must be logged in to update your profile.");
        return;
      }
      await createOrUpdateUserWithUid(user.uid, {
        email: profile.email,
        firstName: profile.firstName,
        lastName: profile.lastName,
        phone: profile.phone,
        streetAddress: profile.street,
        postCode: profile.postCode,
        county: profile.county,
        city: profile.city,
        ...user,
      });
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2500);
    } catch (err) {
      setError("Failed to update profile.");
    }
  };

  return (
    <div
      style={{
        background: "linear-gradient(135deg, var(--primary-500) 0%, var(--cyan-400) 50%, var(--primary-600) 100%)",
        minHeight: "100vh",
        padding: 0,
        position: "relative",
      }}
    >
      <span
        onClick={() => navigate("/")}
        style={{
          position: "absolute",
          top: 18,
          right: 24,
          fontSize: 22,
          color: "#888",
          cursor: "pointer",
          zIndex: 10,
          fontWeight: 400,
          lineHeight: 1,
          transition: "color 0.2s",
        }}
        aria-label="Close profile"
        title="Close"
      >
        &times;
      </span>
      <Container
        fluid
        style={{ maxWidth: 1100, margin: "0 auto", padding: "0" }}
      >
        {/* Logo and Logout */}
        <Row className="align-items-center mb-4" style={{ paddingTop: 32 }}>
          <Col xs="auto">
            <ThemeLogo style={{ width: 100, height: 100, marginRight: 12 }} alt="Logo" />
            <span
              style={{
                fontWeight: 700,
                fontSize: 28,
                verticalAlign: "middle",
                color: "var(--text)",
                letterSpacing: 1,
              }}
            >
              EPCSPARK
            </span>
          </Col>
        </Row>
        {/* Tabs */}
        <Row>
          <Col xs={12}>
            <div
              className="profile-tabs mb-4 mt-2"
              style={{
                display: "flex",
                gap: 32,
                borderBottom: "1px solid rgba(255, 255, 255, 0.3)",
              }}
            >
              <button
                className={`profile-tab-btn${activeTab === "subscriptions" ? " active" : ""}`}
                onClick={() => navigate("/subscription")}
                type="button"
                style={{
                  fontWeight: 700,
                  fontSize: "1.13rem",
                  color: activeTab === "subscriptions" ? "var(--primary-500)" : "var(--text)",
                  background: "none",
                  border: "none",
                  outline: "none",
                  padding: 0,
                  margin: 0,
                  position: "relative",
                  cursor: "pointer",
                }}
              >
                Subscriptions
              </button>
              <button
                className={`profile-tab-btn${activeTab === "profile" ? " active" : ""}`}
                onClick={() => setActiveTab("profile")}
                type="button"
                style={{
                  fontWeight: 700,
                  fontSize: "1.13rem",
                  color: activeTab === "profile" ? "var(--primary-500)" : "var(--text)",
                  background: "none",
                  border: "none",
                  outline: "none",
                  padding: 0,
                  margin: 0,
                  position: "relative",
                  cursor: "pointer",
                }}
              >
                Profile
              </button>
              <button
                className="profile-tab-btn"
                onClick={() => navigate("/change-password")}
                type="button"
                style={{
                  fontWeight: 700,
                  fontSize: "1.13rem",
                  color: "var(--text)",
                  background: "none",
                  border: "none",
                  outline: "none",
                  padding: 0,
                  margin: 0,
                  position: "relative",
                  cursor: "pointer",
                }}
              >
                Change Password
              </button>
            </div>
          </Col>
        </Row>
        {/* Tab Content */}
        {loading ? (
          <div style={{ textAlign: "center", marginTop: 40 }}>Loading...</div>
        ) : error ? (
          <Alert
            variant="danger"
            style={{ fontWeight: 600, fontSize: "1.1rem", textAlign: "center" }}
          >
            {error}
          </Alert>
        ) : (
          <>
            {showSuccess && (
              <Alert
                variant="success"
                style={{
                  fontWeight: 600,
                  fontSize: "1.1rem",
                  textAlign: "center",
                }}
              >
                ✅ Your profile has been updated successfully.
              </Alert>
            )}
            <div
              style={{
                background: "var(--surface-2)",
                border: "1px solid var(--border)",
                borderRadius: 16,
                boxShadow: "0 2px 8px rgba(0,0,0,0.03)",
                padding: "24px",
                marginBottom: 16,
              }}
            >
              <h2
                style={{
                  fontWeight: 700,
                  fontSize: "2rem",
                  color: "var(--text)",
                  marginBottom: 24,
                  textShadow: "0 1px 2px rgba(0,0,0,0.1)",
                  position: "relative",
                  zIndex: 10,
                }}
              >
                Profile
              </h2>
              <Form onSubmit={handleUpdate}>
                <Row className="g-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label style={labelStyle}>First Name</Form.Label>
                      <Form.Control
                        name="firstName"
                        value={profile.firstName}
                        onChange={handleChange}
                        style={inputStyle}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label style={labelStyle}>Last Name</Form.Label>
                      <Form.Control
                        name="lastName"
                        value={profile.lastName}
                        onChange={handleChange}
                        style={inputStyle}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label style={labelStyle}>Email Address</Form.Label>
                      <Form.Control
                        name="email"
                        value={profile.email}
                        onChange={handleChange}
                        style={inputStyle}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label style={labelStyle}>Phone Number</Form.Label>
                      <Form.Control
                        name="phone"
                        value={profile.phone}
                        onChange={handleChange}
                        style={inputStyle}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label style={labelStyle}>Street Address</Form.Label>
                      <Form.Control
                        name="street"
                        value={profile.street}
                        onChange={handleChange}
                        style={inputStyle}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label style={labelStyle}>Post Code</Form.Label>
                      <Form.Control
                        name="postCode"
                        value={profile.postCode}
                        onChange={handleChange}
                        style={inputStyle}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label style={labelStyle}>County</Form.Label>
                      <Form.Control
                        name="county"
                        value={profile.county}
                        onChange={handleChange}
                        style={inputStyle}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label style={labelStyle}>City</Form.Label>
                      <Form.Control
                        name="city"
                        value={profile.city}
                        onChange={handleChange}
                        style={inputStyle}
                      />
                    </Form.Group>
                  </Col>
                  <Col xs={12} className="mt-2">
                    <Button
                      type="submit"
                      style={{
                        width: 160,
                        background: "linear-gradient(135deg, #6d28d9 0%, #4f46e5 45%, #22d3ee 100%)",
                        color: "#fff",
                        fontWeight: 700,
                        fontSize: "1.18rem",
                        border: "none",
                        borderRadius: "12px",
                        padding: "12px 0",
                        margin: "10px 0 0 0",
                        boxShadow: "0 4px 16px rgba(139, 92, 246, 0.25)",
                        cursor: "pointer",
                        transition: "all 0.3s ease",
                      }}
                    >
                      Update
                    </Button>
                  </Col>
                </Row>
              </Form>
            </div>
          </>
        )}
      </Container>
      {/* Tab underline style */}
      <style>{`
        .profile-tab-btn.active {
          color: var(--primary-500) !important;
        }
        .profile-tab-btn.active::after {
          content: '';
          position: absolute;
          left: 0;
          bottom: -8px;
          width: 100%;
          height: 3px;
          background: var(--primary-500);
          border-radius: 2px;
        }
        .profile-tab-btn:not(.active):hover::after {
          content: '';
          position: absolute;
          left: 0;
          bottom: -8px;
          width: 100%;
          height: 3px;
          background: var(--primary-500);
          border-radius: 2px;
        }
        input::placeholder {
          color: var(--text-secondary) !important;
        }
        input:focus::placeholder {
          color: var(--text) !important;
        }
        h2 {
          color: var(--text) !important;
          text-shadow: 0 1px 2px rgba(0,0,0,0.1) !important;
        }
      `}</style>
    </div>
  );
}

const inputStyle = {
  background: "var(--surface-2)",
  border: "1px solid var(--border)",
  borderRadius: 10,
  fontSize: "1.08rem",
  fontWeight: 500,
  color: "var(--text)",
  padding: "16px",
  marginBottom: 0,
  boxShadow: "none",
};

const labelStyle = {
  fontWeight: 700,
  color: "var(--text)",
  fontSize: "1.01rem",
  marginBottom: 6,
};
